function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import SpzaComponent from '@shared/components/spzaComponent';
import { Text, Stack, MessageBar, MessageBarType, mergeStyles, mergeStyleSets, TeachingBubble, DirectionalHint, Icon } from '@fluentui/react';
import { Constants } from '@shared/utils/constants';
import { CatalogOfferType } from '@shared/Models';
import { SpzaInstrumentService } from '@shared/services/telemetry/spza/spzaInstrument';
import { getWindow } from '@shared/services/window';
import { ExternalLink } from '@shared/components/externalLink';
import { BagNextSteps } from '@appsource/components/bagNextSteps';
import { TelemetryImage } from 'components/telemetryImage';
import { getNpsModule } from '@appsource/utils/nps';
import { isBagTransactable } from '@shared/utils/appUtils';
import { getBillingTermTranslated } from '@shared/utils/pricing';
import { convertTermToString } from 'utils/checkoutUtils';
import { NeutralColors } from '@fluentui/theme';
import { logger } from '@src/logger';
import Tooltip from '@shared/components/tooltip';
import { isOneTimePaymentOffer } from '@shared/utils/onetimepaymentoffers';
const completedIconClass = mergeStyles({
    marginRight: '16px'
});
const classes = mergeStyleSets({
    detailsTitle: {
        color: NeutralColors.gray130
    },
    pricingTitle: {
        color: NeutralColors.gray130,
        margin: '16px 0'
    },
    teachingBubbleText: {
        color: NeutralColors.white
    }
});
export class PurchaseBlendsConfirmation extends SpzaComponent {
    componentDidMount() {
        const isSaaSBundle = this.props.checkout.items.some((item)=>{
            var _item_linkedAddIns;
            return (_item_linkedAddIns = item.linkedAddIns) === null || _item_linkedAddIns === void 0 ? void 0 : _item_linkedAddIns.length;
        });
        const payloadDetails = {
            currentView: this.props.config.currentView,
            cartItems: this.props.checkout && this.props.checkout.items,
            isSaaSBundle: false
        };
        if (isSaaSBundle) {
            payloadDetails.isSaaSBundle = isSaaSBundle;
        }
        const payload = {
            page: getWindow().location.href,
            action: Constants.Telemetry.Action.PageLoad,
            actionModifier: Constants.Telemetry.ActionModifier.End,
            details: JSON.stringify(payloadDetails)
        };
        this.instrument.probe(Constants.Telemetry.ProbeName.LogInfo, payload);
        logger.info(payload.details, {
            action: payload.action,
            actionModifier: payload.actionModifier
        });
    }
    renderTeachingBubble() {
        const { shouldRenderTeachingBubble } = this.state;
        const teachingBubbleContent = /*#__PURE__*/ _jsxs(Text, {
            className: classes.teachingBubbleText,
            children: [
                this.context.loc('Checkout_Teaching_Bubble_Content_StartText', 'To manage your licenses anytime, click on your account, open'),
                /*#__PURE__*/ _jsx(ExternalLink, {
                    "data-bi-id": Constants.TeachingBubblePurchaseConfirmation.AdminCenterLinkId,
                    "data-bi-name": Constants.TeachingBubblePurchaseConfirmation.AdminCenterLinkName,
                    className: "boldText link-double-sided-padding",
                    href: Constants.AdminCenterLink,
                    accessibilityEnabled: true,
                    target: "_blank",
                    rel: "noreferrer",
                    onClick: this.logAdminCenterClick,
                    children: this.context.loc('Checkout_Teaching_Bubble_Content_AdminCenterText', 'Admin center')
                }),
                this.context.loc('Checkout_Teaching_Bubble_Content_SelectText', 'and go to'),
                /*#__PURE__*/ _jsx(ExternalLink, {
                    "data-bi-id": Constants.TeachingBubblePurchaseConfirmation.AdminCenterLicensesLinkId,
                    "data-bi-name": Constants.TeachingBubblePurchaseConfirmation.AdminCenterLicensesLinkName,
                    className: "boldText link-double-sided-padding",
                    href: Constants.AdminCenterLicensesLink,
                    target: "_blank",
                    rel: "noreferrer",
                    accessibilityEnabled: true,
                    onClick: this.logAdminCenterLicensesClick,
                    children: this.context.loc('Checkout_Teaching_Bubble_Content_LicensesText', 'Licenses.')
                })
            ]
        });
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("span", {
                    ref: this.teachingBubbleAnchorRef,
                    className: "anchorForTeachingBubble"
                }),
                shouldRenderTeachingBubble && /*#__PURE__*/ _jsx(TeachingBubble, {
                    calloutProps: {
                        directionalHint: DirectionalHint.topRightEdge
                    },
                    target: this.teachingBubbleAnchorRef.current,
                    hasCloseButton: true,
                    closeButtonAriaLabel: "Close",
                    onDismiss: this.toggleTeachingBubble,
                    headline: this.context.loc('Checkout_Teaching_Bubble_Headline', 'Manage your subscriptions'),
                    children: teachingBubbleContent
                })
            ]
        });
    }
    renderImpl() {
        const { item, subRecipient, appImageUri, hasFreeTrial } = this.getPurchaseDetails();
        const isBagOffer = isBagTransactable(this.props.app);
        const { app: { entityId }, isProfessionalService } = this.props;
        const isOneTimePayment = isOneTimePaymentOffer(entityId);
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                !isOneTimePayment && this.renderTeachingBubble(),
                /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "checkout-content",
                        children: /*#__PURE__*/ _jsxs(Stack, {
                            children: [
                                /*#__PURE__*/ _jsx(Stack.Item, {
                                    children: isOneTimePayment ? /*#__PURE__*/ _jsx(Text, {
                                        className: "new-checkout-step-header ms-fontWeight-semibold ms-fontWeight-semibold",
                                        children: this.context.loc('Checkout_Step_Complete_Purchase_OneTimePaymentTeams', 'Activate purchase')
                                    }) : /*#__PURE__*/ _jsx(Text, {
                                        className: "new-checkout-step-header ms-fontWeight-semibold ms-fontWeight-semibold",
                                        children: this.context.loc('Checkout_Step_Complete_Purchase', 'Complete purchase')
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Stack, {
                                    horizontal: true,
                                    className: `thank-you-container`,
                                    children: [
                                        /*#__PURE__*/ _jsx(Stack.Item, {
                                            align: "center",
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: `icon-success-30 ${completedIconClass}`
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Stack.Item, {
                                            align: "center",
                                            children: /*#__PURE__*/ _jsx(Text, {
                                                className: "checkout-complete-configure-title ms-fontWeight-semibold",
                                                children: isOneTimePayment ? this.context.loc('Checkout_OrderConfirmation_ThankYouForYourOrderTitle_OneTimePaymentTeams', 'Thank you for your order. Make sure to activate your purchase!') : isBagOffer ? this.context.loc('Checkout_OrderConfirmation_ThankYouForYourOrderTitle_Bag', 'Thank you for your purchase') : isProfessionalService ? this.context.loc('Checkout_OrderConfirmation_ThankYouForYourOrderTitle_ProfessionalServices', 'Thank you for your order. Contact provider to set up the service.') : hasFreeTrial ? this.context.loc('Checkout_OrderConfirmation_ThankYouForYourOrderTitle_FreeTrial', 'Thank you for your order. Configure your SaaS account to enjoy the free trial') : this.context.loc('Checkout_OrderConfirmation_ThankYouForYourOrderTitle_NonFreeTrial', 'Thank you for your order. Configure your SaaS account to complete the purchase')
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Stack, {
                                    horizontal: true,
                                    tokens: {
                                        childrenGap: 20
                                    },
                                    children: [
                                        appImageUri && /*#__PURE__*/ _jsx(TelemetryImage, {
                                            src: appImageUri,
                                            imgClassName: "img-icon"
                                        }),
                                        /*#__PURE__*/ _jsx(Text, {
                                            className: "purchase-complete-apptitle ms-fontWeight-semibold",
                                            children: item.appTitle
                                        })
                                    ]
                                }),
                                !isProfessionalService && // Using data-testid because MessageBar text render in test issue https://github.com/microsoft/fluentui/issues/17004
                                /*#__PURE__*/ _jsx(Stack.Item, {
                                    "data-testid": "configure-message",
                                    className: "checkout-complete-purchase-banner-warning",
                                    children: this.renderConfigureMessageBar()
                                }),
                                /*#__PURE__*/ _jsxs(Stack, {
                                    horizontal: true,
                                    wrap: true,
                                    tokens: {
                                        childrenGap: 80
                                    },
                                    children: [
                                        this.renderProductDetails(item),
                                        subRecipient && this.renderPaymentDetails(subRecipient)
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Stack, {
                                    tokens: {
                                        childrenGap: 10
                                    },
                                    children: isOneTimePayment ? this.renderImportantToKnowOneTimePayment() : isProfessionalService ? this.renderImportantToKnowProfessionalServices() : isBagOffer ? this.renderNextSteps() : this.renderImportantToKnow()
                                })
                            ]
                        })
                    })
                })
            ]
        });
    }
    renderNextSteps() {
        return /*#__PURE__*/ _jsx(BagNextSteps, {
            context: this.context,
            app: this.props.app
        });
    }
    constructor(props, context){
        var _this_props_checkout_receipt;
        super(props, context), _define_property(this, "context", void 0), _define_property(this, "instrument", void 0), _define_property(this, "teachingBubbleAnchorRef", null), _define_property(this, "getPurchaseDetails", ()=>{
            var _item_unitPrice;
            const { checkout: { items, billingAddress } } = this.props;
            const item = items[0];
            return {
                item,
                subRecipient: billingAddress,
                appImageUri: item === null || item === void 0 ? void 0 : item.appImageUri,
                hasFreeTrial: (item === null || item === void 0 ? void 0 : (_item_unitPrice = item.unitPrice) === null || _item_unitPrice === void 0 ? void 0 : _item_unitPrice.freeDurationDays) > 0
            };
        }), _define_property(this, "toggleTeachingBubble", ()=>{
            this.setState({
                shouldRenderTeachingBubble: false
            });
        }), _define_property(this, "renderPaymentDetails", (subRecipient)=>{
            const { companyName, country, postalCode, city, region, addressLine1, addressLine2, firstName, lastName } = subRecipient;
            const title = firstName && lastName ? `${firstName} ${lastName}` : companyName;
            return /*#__PURE__*/ _jsxs(Stack, {
                className: "minWidth-270",
                tokens: {
                    childrenGap: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            block: true,
                            className: "ms-fontWeight-semibold",
                            children: this.context.loc('Checkout_OrderConfirmation_InvoiceDetailsLabel', 'Invoice details')
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.pricingTitle,
                                children: this.context.loc('Checkout_OrderConfirmation_SoldToText', 'Sold to')
                            }),
                            subRecipient && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            className: "description",
                                            children: title
                                        })
                                    }),
                                    addressLine1 && /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            children: addressLine1
                                        })
                                    }),
                                    addressLine2 && /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            children: addressLine2
                                        })
                                    }),
                                    city && region && postalCode && /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            nowrap: true,
                                            children: `${city}, ${region.toUpperCase()} ${postalCode}`
                                        })
                                    }),
                                    country && /*#__PURE__*/ _jsx(Stack.Item, {
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            children: country
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            });
        }), _define_property(this, "renderProductDetails", (item)=>{
            var _checkout_receipt, _checkout_receipt1, _checkout_receipt2;
            const { config, checkout, checkout: { items }, app: { entityId }, billingCountry, isProfessionalService } = this.props;
            const { hasFreeTrial, isQuantifiable, id: planId, termId, product } = items[0];
            const calloutProps = {
                gapSpace: 0
            };
            const isOneTimePayment = isOneTimePaymentOffer(entityId);
            return /*#__PURE__*/ _jsxs(Stack, {
                className: "minWidth-270",
                tokens: {
                    childrenGap: 16
                },
                children: [
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            className: "new-checkout-step-header ms-fontWeight-semibold",
                            children: this.context.loc('Checkout_OrderConfirmation_OfferDetailsLabel', 'Offer details')
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.detailsTitle,
                                children: this.context.loc('Checkout_OrderConfirmation_OfferLabel', 'Offer')
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                children: item.appTitle
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.detailsTitle,
                                children: this.context.loc('Checkout_OrderConfirmation_PlanLabel', 'Plan')
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                children: item.title
                            })
                        ]
                    }),
                    !isOneTimePayment && !isProfessionalService && /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.detailsTitle,
                                children: this.context.loc('Checkout_OrderConfirmation_BillingTermLabel', 'Billing term')
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                children: getBillingTermTranslated({
                                    context: this.context,
                                    term: item.termDuration
                                })
                            })
                        ]
                    }),
                    !isProfessionalService && /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.detailsTitle,
                                children: this.context.loc('Checkout_PriceAndPaymentOptions', 'Price + payment options')
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                nowrap: true,
                                children: `${convertTermToString({
                                    context: this.context,
                                    term: product.getTermByTermId({
                                        planId,
                                        termId
                                    }),
                                    countryCode: billingCountry,
                                    isQuantifiable,
                                    hasFreeTrial,
                                    locale: this.props.config.locale
                                })} + ${this.context.loc('Checkout_OrderConfirmation_TaxLabel', 'tax')}`
                            })
                        ]
                    }),
                    !isOneTimePayment && !isProfessionalService && /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsxs(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.detailsTitle,
                                children: [
                                    `${this.context.loc('Checkout_BillingOptions_IsRecurring', 'Auto-renew')} `,
                                    /*#__PURE__*/ _jsx(Tooltip, {
                                        tooltipHostProps: {
                                            content: this.context.loc('Checkout_PriceAndBilling_RecurringInfoBalloon'),
                                            calloutProps
                                        },
                                        children: /*#__PURE__*/ _jsx(Icon, {
                                            iconName: "Info",
                                            style: {
                                                cursor: 'pointer'
                                            }
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                children: item.recurringBilling ? this.context.loc('On', 'On') : this.context.loc('Off', 'Off')
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Stack.Item, {
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                variant: "smallPlus",
                                className: classes.detailsTitle,
                                children: this.context.loc('Checkout_OrderConfirmation_TotalLabel', 'Total')
                            }),
                            !isNaN(Number((_checkout_receipt = checkout.receipt) === null || _checkout_receipt === void 0 ? void 0 : _checkout_receipt.orderSummary.total)) && /*#__PURE__*/ _jsx(Text, {
                                block: true,
                                children: Number((_checkout_receipt1 = checkout.receipt) === null || _checkout_receipt1 === void 0 ? void 0 : _checkout_receipt1.orderSummary.total).toLocaleString(config.locale, {
                                    style: 'currency',
                                    currency: (_checkout_receipt2 = checkout.receipt) === null || _checkout_receipt2 === void 0 ? void 0 : _checkout_receipt2.orderSummary.currency,
                                    maximumFractionDigits: 2
                                })
                            })
                        ]
                    })
                ]
            });
        }), _define_property(this, "renderImportantToKnow", ()=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        className: "new-checkout-step-header ms-fontWeight-semibold displayBlock mt-20",
                        children: this.context.loc('Checkout_OrderConfirmation_ImportantToKnowText', 'Important to know')
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_BillingWillStartText', "Billing will start after this SaaS account is configured on the publisher's website.")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_IfNoActionText', 'If no action is taken within 30 days, this SaaS subscription will be automatically deleted.')
                        })
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsxs(Text, {
                            children: [
                                this.context.loc('Checkout_OrderConfirmation_YourSaaSSubscriptionText', 'Your SaaS subscription will appear in'),
                                /*#__PURE__*/ _jsx(ExternalLink, {
                                    "data-bi-id": 'pastOrders',
                                    "data-bi-name": 'My orders link post checkout',
                                    className: "c-hyperlink link-double-sided-padding",
                                    href: Constants.MyOrdersLink,
                                    accessibilityEnabled: true,
                                    target: "_blank",
                                    rel: "noreferrer",
                                    onClick: this.logMyOrdersClick,
                                    children: this.context.loc('Checkout_OrderConfirmation_MyOrdersLinkText', 'My orders')
                                }),
                                this.context.loc('Checkout_OrderConfirmation_InTheAdminCenterText', 'in the Admin center.')
                            ]
                        })
                    })
                ]
            })), _define_property(this, "renderImportantToKnowOneTimePayment", ()=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        className: "new-checkout-step-header ms-fontWeight-semibold displayBlock mt-20",
                        children: this.context.loc('Checkout_OrderConfirmation_ImportantToKnowText', 'Important to know')
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_BillingWillStartText_OneTimePaymentTeams', 'Billing will start after you activate your purchase below.')
                        })
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_IfNoActionText_OneTimePaymentTeams', 'If no action is taken within 30 days, this order will be automatically canceled.')
                        })
                    })
                ]
            })), _define_property(this, "renderImportantToKnowProfessionalServices", ()=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        className: "new-checkout-step-header ms-fontWeight-semibold displayBlock mt-20",
                        children: this.context.loc('Checkout_OrderConfirmation_ImportantToKnowText', 'Important to know')
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_BillingWillStartText_ProfessionalServices', 'Your purchase was completed successfully and billing will now start.')
                        })
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_IfNoActionText_ProfessionalServices', 'If you haven’t already, contact the service provider to set up the service.')
                        })
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_PurcahseAgain_ProfessionalServices', 'To purchase again, contact the publisher for a new Private Offer.')
                        })
                    }),
                    /*#__PURE__*/ _jsx(Stack.Item, {
                        children: /*#__PURE__*/ _jsx(Text, {
                            children: this.context.loc('Checkout_OrderConfirmation_RePurchase_ProfessionalServices', 'This service can’t be re-purchased within this offer.')
                        })
                    })
                ]
            })), _define_property(this, "renderConfigureMessageBar", ()=>{
            if (isBagTransactable(this.props.app)) {
                return /*#__PURE__*/ _jsx(MessageBar, {
                    messageBarType: MessageBarType.success,
                    children: this.context.loc('Checkout_Complete_Bag_Info_Banner', 'Your order was processed successfully.')
                });
            } else {
                var _this_props_checkout_publisherToken, _this_props_checkout_publisherToken1;
                const { app: { entityId } } = this.props;
                const isOneTimePayment = isOneTimePaymentOffer(entityId);
                const isSaaSOfferType = this.props.checkout.items.some((item)=>item.product.offerType === CatalogOfferType.SaaS);
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        isOneTimePayment ? /*#__PURE__*/ _jsxs(MessageBar, {
                            messageBarType: MessageBarType.warning,
                            isMultiline: true,
                            children: [
                                this.context.loc('Checkout_Complete_Warning_Banner_OneTimePaymentTeams_part1', 'To complete the process,'),
                                /*#__PURE__*/ _jsx(ExternalLink, {
                                    className: "c-hyperlink",
                                    href: ((_this_props_checkout_publisherToken = this.props.checkout.publisherToken) === null || _this_props_checkout_publisherToken === void 0 ? void 0 : _this_props_checkout_publisherToken.offerUriWithToken) || '',
                                    accessibilityEnabled: true,
                                    target: "_blank",
                                    rel: "noreferrer",
                                    onClickHandle: ()=>this.onConfigureNowLinkClick(),
                                    children: this.context.loc('Checkout_Complete_Warning_Banner_OneTimePaymentTeams_part2', 'activate your purchase')
                                }),
                                '.'
                            ]
                        }) : /*#__PURE__*/ _jsxs(MessageBar, {
                            messageBarType: MessageBarType.warning,
                            isMultiline: true,
                            children: [
                                this.context.loc('Checkout_Complete_Warning_Banner_part1', 'To complete the purchase,'),
                                /*#__PURE__*/ _jsx(ExternalLink, {
                                    className: "c-hyperlink",
                                    href: ((_this_props_checkout_publisherToken1 = this.props.checkout.publisherToken) === null || _this_props_checkout_publisherToken1 === void 0 ? void 0 : _this_props_checkout_publisherToken1.offerUriWithToken) || '',
                                    accessibilityEnabled: true,
                                    target: "_blank",
                                    rel: "noreferrer",
                                    onClickHandle: ()=>this.onConfigureNowLinkClick(),
                                    children: this.context.loc('Checkout_Complete_Warning_Banner_part2', 'configure your SaaS account')
                                }),
                                " ",
                                this.context.loc('Checkout_Complete_Warning_Banner_part3', "on the publisher's website."),
                                /*#__PURE__*/ _jsx("br", {}),
                                this.context.loc('Checkout_Complete_Warning_Banner_part4', "We've sent you an email confirmation with the instructions on configuring this SaaS account.")
                            ]
                        }),
                        isSaaSOfferType && /*#__PURE__*/ _jsx(MessageBar, {
                            messageBarType: MessageBarType.info,
                            isMultiline: true,
                            children: this.context.loc('Checkout_OrderConfirmation_ExternalSiteNoticeText', "Clicking on 'Configure account now' will redirect you outside Microsoft AppSource to an independent publisher's website.")
                        })
                    ]
                });
            }
        }), _define_property(this, "logMyOrdersClick", ()=>{
            const payload = {
                page: getWindow().location.href,
                action: Constants.Telemetry.Action.Click,
                actionModifier: Constants.Telemetry.ActionModifier.MyOrders
            };
            SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
            logger.info('', {
                action: payload.action,
                actionModifier: payload.actionModifier
            });
        }), _define_property(this, "logAdminCenterClick", ()=>{
            var _getWindow_location, _getWindow;
            const payload = {
                page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_location = _getWindow.location) === null || _getWindow_location === void 0 ? void 0 : _getWindow_location.href,
                action: Constants.Telemetry.Action.Click,
                actionModifier: Constants.Telemetry.ActionModifier.AdminCenter
            };
            SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
            logger.info(payload.details, {
                action: payload.action,
                actionModifier: payload.actionModifier
            });
        }), _define_property(this, "logAdminCenterYourProductsClick", ()=>{
            var _getWindow_location, _getWindow;
            const payload = {
                page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_location = _getWindow.location) === null || _getWindow_location === void 0 ? void 0 : _getWindow_location.href,
                action: Constants.Telemetry.Action.Click,
                actionModifier: Constants.Telemetry.ActionModifier.AdminCenterYourProducts
            };
            SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
            logger.info(payload.details, {
                action: payload.action,
                actionModifier: payload.actionModifier
            });
        }), _define_property(this, "logAdminCenterLicensesClick", ()=>{
            var _getWindow_location, _getWindow;
            const payload = {
                page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_location = _getWindow.location) === null || _getWindow_location === void 0 ? void 0 : _getWindow_location.href,
                action: Constants.Telemetry.Action.Click,
                actionModifier: Constants.Telemetry.ActionModifier.AdminCenterLicenses
            };
            SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
            logger.info(payload.details, {
                action: payload.action,
                actionModifier: payload.actionModifier
            });
        }), _define_property(this, "onConfigureNowLinkClick", ()=>{
            var _checkout_items, _getWindow_location, _getWindow, _checkout_publisherToken, _this_props_checkout_publisherToken, _getNpsModule;
            const { checkout, checkoutId } = this.props;
            const cartItem = (_checkout_items = checkout.items) === null || _checkout_items === void 0 ? void 0 : _checkout_items[0];
            const { bigId = '', entityId = '', skuId = '', appTitle = '', title: planTitle = '' } = cartItem || {};
            const payload = {
                appName: entityId,
                page: (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow_location = _getWindow.location) === null || _getWindow_location === void 0 ? void 0 : _getWindow_location.href,
                action: Constants.Telemetry.Action.Checkout,
                actionModifier: Constants.Telemetry.ActionModifier.ConfigureNowLink,
                details: JSON.stringify({
                    offerURI: (_checkout_publisherToken = checkout.publisherToken) === null || _checkout_publisherToken === void 0 ? void 0 : _checkout_publisherToken.publisherOfferBaseUri,
                    cartItem: {
                        bigId,
                        entityId,
                        skuId,
                        appTitle,
                        planTitle,
                        checkoutId
                    }
                })
            };
            SpzaInstrumentService.getProvider().probe('logTelemetryInfo', payload);
            logger.info(payload.details, {
                appName: entityId,
                action: payload.action,
                actionModifier: payload.actionModifier
            });
            if ((_this_props_checkout_publisherToken = this.props.checkout.publisherToken) === null || _this_props_checkout_publisherToken === void 0 ? void 0 : _this_props_checkout_publisherToken.offerUriWithToken) {
                var // eslint-disable-next-line security/detect-non-literal-fs-filename
                _getWindow1;
                (_getWindow1 = getWindow()) === null || _getWindow1 === void 0 ? void 0 : _getWindow1.open(this.props.checkout.publisherToken.offerUriWithToken, '_blank');
            }
            (_getNpsModule = getNpsModule()) === null || _getNpsModule === void 0 ? void 0 : _getNpsModule.ctaClicked();
        });
        this.instrument = SpzaInstrumentService.getProvider();
        this.teachingBubbleAnchorRef = /*#__PURE__*/ React.createRef();
        const { app, isProfessionalService } = this.props;
        const isBagOffer = isBagTransactable(app);
        this.state = {
            offerNotFoundError: false,
            previewOfferNotFoundError: false,
            testHeadersError: false,
            customMetersError: false,
            riskPrepaidCardError: false,
            assetSubscriptionId: (_this_props_checkout_receipt = this.props.checkout.receipt) === null || _this_props_checkout_receipt === void 0 ? void 0 : _this_props_checkout_receipt.lineItems[0].assetId,
            shouldRenderTeachingBubble: isBagOffer
        };
        if (this.state.assetSubscriptionId && !isBagOffer && !isProfessionalService) {
            this.props.generatePublisherToken(this.state.assetSubscriptionId);
        }
    }
}
PurchaseBlendsConfirmation.contextTypes = {
    loc: PropTypes.func,
    locParams: PropTypes.func,
    renderErrorModal: PropTypes.func
};
